/**
 * AUTOCONSTRAIN settings dialog (Faz 6.6, OCS AutoConstrainSettings):
 * inference tolerances and toggles. Non-finite input collapses back to the
 * previous value through the store's sanitization (OCS parity).
 */

import { useTranslation } from 'react-i18next';
import {
    HStack,
    Input,
    Switch,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useConstraintStore } from '../../core/constraints/constraintStore';
import { useAppStore } from '../../state/store';
import { ManageDialogFrame, ManageDialogCloseFooter } from './ManageDialogFrame';

function NumberField({ label, value, onChange, step = 0.01, min = 0 }: {
    label: string; value: number; onChange: (v: number) => void; step?: number; min?: number;
}) {
    return (
        <HStack justify="space-between" spacing={3} w="100%">
            <Text fontSize="sm" color="text.secondary" flexShrink={0}>{label}</Text>
            <Input
                type="number"
                size="sm"
                maxW="110px"
                value={value}
                step={step}
                min={min}
                fontFamily="mono"
                bg="bg.canvas"
                borderColor="border.base"
                color="text.primary"
                onChange={(e) => {
                    const v = Number(e.target.value);
                    if (Number.isFinite(v) && v >= min) onChange(v);
                }}
            />
        </HStack>
    );
}

export function AutoConstrainDialog() {
    const { t } = useTranslation();
    const onClose = useAppStore((s) => s.closeManageDialog);
    const settings = useConstraintStore((s) => s.autoConstrain);
    const setSetting = useConstraintStore((s) => s.setAutoConstrainSetting);

    return (
        <ManageDialogFrame
            title={t('constraints.autoConstrain.title')}
            isOpen
            onClose={onClose}
            size="md"
            footer={<ManageDialogCloseFooter onClose={onClose} />}
        >
            <VStack align="stretch" spacing={4} py={2}>
                <NumberField
                    label={t('constraints.autoConstrain.distanceTolerance')}
                    value={settings.distanceTolerance}
                    onChange={(v) => setSetting('distanceTolerance', v)}
                />
                <NumberField
                    label={t('constraints.autoConstrain.angleToleranceDeg')}
                    value={settings.angleToleranceDeg}
                    onChange={(v) => setSetting('angleToleranceDeg', v)}
                />
                <HStack justify="space-between" w="100%">
                    <Text fontSize="sm" color="text.secondary">{t('constraints.autoConstrain.tangentMustSharePoint')}</Text>
                    <Switch
                        size="sm"
                        isChecked={settings.tangentMustSharePoint}
                        onChange={(e) => setSetting('tangentMustSharePoint', e.target.checked)}
                    />
                </HStack>
                <HStack justify="space-between" w="100%">
                    <Text fontSize="sm" color="text.secondary">{t('constraints.autoConstrain.perpendicularMustIntersect')}</Text>
                    <Switch
                        size="sm"
                        isChecked={settings.perpendicularMustIntersect}
                        onChange={(e) => setSetting('perpendicularMustIntersect', e.target.checked)}
                    />
                </HStack>
                <HStack justify="space-between" w="100%">
                    <Text fontSize="sm" color="text.secondary">{t('constraints.autoConstrain.equalEnabled')}</Text>
                    <Switch
                        size="sm"
                        isChecked={settings.equalEnabled}
                        onChange={(e) => setSetting('equalEnabled', e.target.checked)}
                    />
                </HStack>
                <Text fontSize="xs" color="text.secondary">
                    {t('constraints.autoConstrain.coincidentNote')}
                </Text>
            </VStack>
        </ManageDialogFrame>
    );
}
